import { useEffect, useState } from 'react';
import { Link, useParams, useLocation } from 'react-router-dom';
import Navbar from '@/components/feature/Navbar';
import Footer from '@/components/feature/Footer';
import { categories, comics } from '@/mocks/comics';
import { generateWebPageSchema, generateBreadcrumbListSchema } from '@/utils/seo';

export default function CategoryPage() {
  const { slug } = useParams<{ slug: string }>();
  const { pathname } = useLocation();
  const [activeSlug, setActiveSlug] = useState(slug || 'all');

  useEffect(() => {
    window.scrollTo({ top: 0, behavior: 'instant' });
    setActiveSlug(slug || 'all');
  }, [slug, pathname]);

  const currentCategory = categories.find((c) => c.slug === activeSlug);
  const filteredComics = activeSlug === 'all'
    ? comics
    : activeSlug === 'completed'
      ? comics.filter((c) => c.category === 'completed')
      : comics.filter((c) => c.category === activeSlug);

  const catName = currentCategory?.name || '全部漫画';
  const pageSchema = generateWebPageSchema(
    `${catName} - 韩国漫画网`,
    `浏览韩国漫画网的${catName}分类，发现海量免费韩漫资源，支持完整在线观看。`,
    `https://www.example.com/category/${activeSlug}`
  );
  const breadcrumbSchema = generateBreadcrumbListSchema([
    { name: '首页', url: 'https://www.example.com/' },
    { name: catName, url: `https://www.example.com/category/${activeSlug}` },
  ]);

  return (
    <div className="min-h-screen flex flex-col bg-background-50">
      <script type="application/ld+json" dangerouslySetInnerHTML={{ __html: JSON.stringify(pageSchema) }} />
      <script type="application/ld+json" dangerouslySetInnerHTML={{ __html: JSON.stringify(breadcrumbSchema) }} />
      <Navbar />
      <main className="flex-1">
        <div className="bg-background-100 py-8 md:py-10 px-4 md:px-6">
          <div className="max-w-7xl mx-auto">
            <nav className="flex items-center gap-2 text-sm text-foreground-500 mb-4" aria-label="面包屑导航">
              <Link to="/" className="hover:text-primary-600 transition-colors">首页</Link>
              <i className="ri-arrow-right-s-line text-xs"></i>
              <span className="text-foreground-700 font-medium">{currentCategory?.name || '全部漫画'}</span>
            </nav>
            <h1 className="text-2xl md:text-3xl font-bold text-foreground-900 font-heading">
              {currentCategory?.name || '全部漫画'}
            </h1>
            <p className="text-sm text-foreground-500 mt-2">
              共 {filteredComics.length} 部作品
            </p>
          </div>
        </div>

        <div className="max-w-7xl mx-auto px-4 md:px-6 py-8">
          <div className="flex flex-wrap gap-2 mb-8">
            {categories.map((cat) => (
              <Link
                key={cat.slug}
                to={`/category/${cat.slug}`}
                className={`px-4 py-2 rounded-full text-sm font-medium transition-all duration-200 whitespace-nowrap ${
                  activeSlug === cat.slug
                    ? 'bg-primary-500 text-background-50'
                    : 'bg-background-100 text-foreground-600 hover:bg-primary-50 hover:text-primary-600'
                }`}
              >
                <i className={`${cat.icon} mr-1.5`}></i>
                {cat.name}
              </Link>
            ))}
          </div>

          {filteredComics.length === 0 ? (
            <div className="text-center py-20">
              <div className="w-16 h-16 mx-auto mb-4 flex items-center justify-center rounded-full bg-background-100">
                <i className="ri-inbox-line text-2xl text-foreground-400"></i>
              </div>
              <p className="text-foreground-500">该分类暂无漫画</p>
            </div>
          ) : (
            <div className="grid grid-cols-2 sm:grid-cols-3 md:grid-cols-4 lg:grid-cols-5 gap-4 md:gap-5">
              {filteredComics.map((comic) => (
                <Link
                  key={comic.id}
                  to={`/article/${comic.id}`}
                  className="group"
                >
                  <div className="relative overflow-hidden rounded-lg mb-3 aspect-[3/4]">
                    <img
                      src={comic.cover}
                      alt={comic.title}
                      className="w-full h-full object-cover object-top group-hover:scale-105 transition-transform duration-300"
                    />
                    <div className="absolute bottom-0 inset-x-0 bg-gradient-to-t from-black/70 to-transparent p-3">
                      <div className="flex items-center gap-1 text-white text-xs">
                        <i className="ri-star-fill text-accent-400 text-xs"></i>
                        <span>{comic.rating}</span>
                        <span className="mx-1">·</span>
                        <span>{comic.views.toLocaleString()} 阅读</span>
                      </div>
                    </div>
                    {comic.isHot && (
                      <div className="absolute top-2 left-2 px-2 py-0.5 bg-accent-500 text-background-50 text-xs font-medium rounded-full">
                        热门
                      </div>
                    )}
                  </div>
                  <h3 className="text-sm font-medium text-foreground-800 group-hover:text-primary-600 transition-colors duration-200 line-clamp-1">
                    {comic.title}
                  </h3>
                  <div className="flex items-center gap-2 mt-1.5">
                    <span className="text-xs text-foreground-500">{comic.author}</span>
                    <span className="text-xs text-foreground-400">·</span>
                    <span className="text-xs text-accent-600">{comic.tags[0]}</span>
                  </div>
                </Link>
              ))}
            </div>
          )}
        </div>
      </main>
      <Footer />
    </div>
  );
}